import { operationIds } from "@gen/app-access-config";
import {
  ConnectedAccountVerification_status,
  CoreQuery,
  Query,
} from "@gen/app-query";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { type MouseEventHandler, type ReactNode } from "react";

import { HeadingContainer } from "~/components/heading_container";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { useCanExecuteOperation } from "~/contexts/auth_query";
import { useConfig } from "~/contexts/config";

interface AccountStatusBannerProps {
  clientId: string;
  connectedAccountId: string | undefined;
  isAdmin?: boolean;
}

export function AccountStatusBanner({
  clientId,
  connectedAccountId,
  isAdmin = false,
}: AccountStatusBannerProps) {
  const canExecuteOperation = useCanExecuteOperation();
  const { supportEmail, solutionsEmail } = useConfig();
  // Client admins and readers only get informative banners, while realm admins
  // and client owners can also run the KYC onboarding.
  const canViewStatus = canExecuteOperation(
    operationIds["core.getClientConnectedAccount"],
  );
  const canVerifyAccount = canExecuteOperation(
    operationIds["core.getClientOnboardingSession"],
  );
  // Only client owners can link their own Stripe account to a client that doesn't have one yet.
  const canSetStripeAccount = canExecuteOperation(
    operationIds["core.setClientStripeAccount"],
  );
  const hasConnectedAccount = Boolean(connectedAccountId);
  const shouldFetch = canViewStatus && hasConnectedAccount;

  // Refetch on every mount, so the banner switches to the account status right
  // after the KYC is completed, instead of showing the cached status.
  const coreConnectedAccountQuery =
    Query.useCore_getClientConnectedAccountQuery(clientId, {
      enabled: shouldFetch && !isAdmin,
      refetchOnMount: "always",
    });
  const adminConnectedAccountQuery =
    CoreQuery.useAdmin_getClientConnectedAccountQuery(clientId, {
      enabled: shouldFetch && isAdmin,
      refetchOnMount: "always",
    });
  const { data: connectedAccountData, isFetchedAfterMount } = isAdmin
    ? adminConnectedAccountQuery
    : coreConnectedAccountQuery;

  if (!canViewStatus || connectedAccountId === undefined) {
    return null;
  }

  if (!hasConnectedAccount) {
    if (canSetStripeAccount) {
      return (
        <ActionRequiredBanner
          clientId={clientId}
          canVerifyAccount
          hasConnectedAccount={false}
        />
      );
    }
    return (
      <VerificationBanner status="No linked account">
        <Typography variant="body2" color="text.secondary">
          A Trustap account must be linked to this client to enable payment
          processing. Contact <EmailLink email={solutionsEmail} /> to get set
          up.
        </Typography>
      </VerificationBanner>
    );
  }

  if (!isFetchedAfterMount || !connectedAccountData) {
    return null;
  }

  switch (connectedAccountData.verification_status) {
    case ConnectedAccountVerification_status.Verified:
      if (!canVerifyAccount) {
        return null;
      }
      return (
        <HeadingContainer
          title={
            <>
              <GppGoodOutlinedIcon /> Account Status
            </>
          }
          variant="h5"
          borderType="dashed"
        >
          <Button
            component={Link}
            to={`/clients/${clientId}/kyc`}
            variant="text"
            startIcon={<PublicSvgIcon name="manage_accounts" inheritColor />}
            data-testid="account-status-manage-button"
          >
            Manage Profile
          </Button>
        </HeadingContainer>
      );
    case ConnectedAccountVerification_status.Disabled:
      return (
        <VerificationBanner status="Disabled">
          <Typography variant="body2" color="text.secondary">
            All account activity is currently suspended. Please contact{" "}
            <EmailLink email={supportEmail} /> for details.
          </Typography>
        </VerificationBanner>
      );
    case ConnectedAccountVerification_status.Restricted:
      return (
        <VerificationBanner
          status="Restricted"
          action={
            canVerifyAccount && (
              <VerifyButton clientId={clientId}>Verify account</VerifyButton>
            )
          }
        >
          <Typography variant="body2" color="text.secondary">
            Please add outstanding account verification information to your
            Trustap profile.
            <br />
            Payments and payouts may be disabled until verification is complete.
          </Typography>
        </VerificationBanner>
      );
    default:
      return (
        <ActionRequiredBanner
          clientId={clientId}
          canVerifyAccount={canVerifyAccount}
          hasConnectedAccount
        />
      );
  }
}

interface ActionRequiredBannerProps {
  clientId: string;
  canVerifyAccount: boolean;
  hasConnectedAccount: boolean;
}

function ActionRequiredBanner({
  clientId,
  canVerifyAccount,
  hasConnectedAccount,
}: ActionRequiredBannerProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { supportEmail } = useConfig();
  const { mutate: setStripeAccount, isPending: isSettingStripeAccount } =
    Query.useCore_setClientStripeAccountMutation(clientId, {
      onSuccess: (client) => {
        Query.setCore_getClientData(queryClient, () => client, clientId);
        navigate({ to: "/clients/$clientId/kyc", params: { clientId } });
      },
      // The API error messages (e.g. about Stripe platform mismatches) aren't
      // meaningful to the owner, so a generic message is shown instead.
      onError: () => {
        enqueueSnackbar(
          `Your profile couldn't be created. Please try again later or contact ${supportEmail}.`,
          { variant: "error" },
        );
      },
      meta: { suppressError: true },
    });

  return (
    <VerificationBanner
      status="Action required"
      action={
        canVerifyAccount && (
          <VerifyButton
            clientId={clientId}
            onClick={(event) => {
              // Link the owner's Stripe account to the client before starting the KYC.
              if (!hasConnectedAccount) {
                event.preventDefault();
                setStripeAccount({});
              }
            }}
            loading={isSettingStripeAccount}
          >
            Create Profile
          </VerifyButton>
        )
      }
    >
      <Typography variant="body2" color="text.secondary">
        Verify your business to start processing payments. Takes about 5
        minutes.
      </Typography>
      <Typography variant="body3" color="text.secondary">
        Have your company details and a photo ID ready.
      </Typography>
    </VerificationBanner>
  );
}

interface VerificationBannerProps {
  status: string;
  action?: ReactNode;
  children: ReactNode;
}

function VerificationBanner({
  status,
  action,
  children,
}: VerificationBannerProps) {
  return (
    <Stack
      direction="row"
      data-testid="account-status-banner"
      sx={{
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        py: 2.5,
        px: 5,
        pl: 4.5,
        bgcolor: "primary.50",
        borderLeft: 4,
        borderColor: "primary.main",
        borderRadius: 2,
      }}
    >
      <Stack sx={{ flex: "1 1 auto", minWidth: 0, gap: 2 }}>
        <Stack
          direction="row"
          sx={{
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 2.5,
            rowGap: 1.5,
          }}
        >
          <Typography variant="h6" component="h2">
            Account Verification
          </Typography>
          <Chip
            label={status}
            color="warning"
            sx={{ color: "common.white" }}
            data-testid="account-status-chip"
          />
        </Stack>
        {children}
      </Stack>
      {action}
    </Stack>
  );
}

interface VerifyButtonProps {
  clientId: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  loading?: boolean;
  children: ReactNode;
}

function VerifyButton({
  clientId,
  onClick,
  loading = false,
  children,
}: VerifyButtonProps) {
  return (
    <Button
      component={Link}
      to={`/clients/${clientId}/kyc`}
      onClick={onClick}
      loading={loading}
      variant="contained"
      size="large"
      disableElevation
      startIcon={<GppGoodOutlinedIcon />}
      data-testid="account-status-verify-button"
      sx={{
        flexShrink: 0,
        flexDirection: "row",
        py: { xs: 0.75, sm: 1 },
        px: { xs: 2, sm: 2.75 },
        fontSize: { xs: 14, sm: 16 },
        lineHeight: { xs: "24px", sm: "26px" },
        letterSpacing: "0.46px",
        "& .MuiButton-startIcon": { ml: 0 },
        "& .MuiButton-startIcon > svg": { fontSize: { xs: 20, sm: 24 } },
      }}
    >
      {children}
    </Button>
  );
}

function EmailLink({ email }: { email: string }) {
  return (
    <MuiLink
      href={`mailto:${email}`}
      variant="inherit"
      sx={{ fontWeight: 700 }}
    >
      {email}
    </MuiLink>
  );
}
